import React, { useEffect, useState, useCallback } from 'react';
import { message } from 'antd';
import {
  CREATE_ERROR,
  DELETE_ERROR,
  EDIT_ERROR,
  FETCH_ERROR,
  REQUEST_LOADING,
  SUCCESS_CREATE,
  SUCCESS_DELETE,
  SUCCESS_UPDATE,
} from 'constants/alerts';
import { RecordInfoDrawer } from 'components/RecordInfoDrawer';
import { TableHeader } from 'components/TableHeader';
import { ServicesService } from 'services/ServicesService';
import { useHeaderChanger } from 'hooks/useHeaderChanger';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import { usePagination } from 'hooks/usePagination';
import { useDrawers } from 'hooks/useDrawers';
import { formatDate } from 'utils';
import { DiscountForm } from './DiscountForm';
import { DiscountTable } from './DiscountTable';

function Discount() {
  useDocumentTitle('Скидки услуг');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const { setCount } = useHeaderChanger('Скидки услуг');
  const { page, perPage, totalRows, setPagination, setTotalRows } =
    usePagination();
  const {
    state: { drawerVisible, formIsActive, activeRecord },
    openDrawer,
    closeDrawer,
    openCreateForm,
    openEditForm,
    closeForm,
  } = useDrawers();

  const fetchItems = useCallback(async () => {
    if (!page || !perPage) {
      return;
    }

    setLoading(true);

    try {
      const { items, total } = await ServicesService.getDiscount({
        page,
        perPage,
      });

      setItems(items);
      setCount(total);
      setTotalRows(total);
    } catch (err) {
      message.error(FETCH_ERROR);
    } finally {
      setLoading(false);
    }
  }, [page, perPage]);

  useEffect(() => fetchItems(), [fetchItems]);

  const handleCreate = async (data) => {
    const hide = message.loading(REQUEST_LOADING);

    try {
      const isCreated = await ServicesService.createDiscount(data);

      if (isCreated) {
        closeForm();
        fetchItems();
        message.success(SUCCESS_CREATE);
      }
    } catch (err) {
      message.error(CREATE_ERROR);
    } finally {
      hide();
    }
  };

  const handleDelete = async () => {
    const hide = message.loading(REQUEST_LOADING);

    try {
      const isDeleted = await ServicesService.deleteDiscount(activeRecord.id);

      if (isDeleted) {
        closeDrawer();
        fetchItems();
        message.success(SUCCESS_DELETE);
      }
    } catch (err) {
      message.error(DELETE_ERROR);
    } finally {
      hide();
    }
  };

  const handleUpdate = async (data, id) => {
    const hide = message.loading(REQUEST_LOADING);

    try {
      const isUpdated = await ServicesService.updateDiscount(data, id);

      if (isUpdated) {
        closeForm();
        fetchItems();
        message.success(SUCCESS_UPDATE);
      }
    } catch (err) {
      message.error(EDIT_ERROR);
    } finally {
      hide();
    }
  };

  return (
    <>
      <TableHeader onCreate={openCreateForm} pageId="discount" />

      <DiscountTable
        items={items}
        loading={loading}
        current={page}
        pageSize={perPage}
        onRowClick={openDrawer}
        total={totalRows}
        onChange={setPagination}
      />

      <RecordInfoDrawer
        record={activeRecord}
        drawerTitle={activeRecord?.title}
        pageId="discount"
        recordFields={[
          { label: 'Скидка', key: 'percent' },
          {
            label: 'Дата начала',
            key: 'start_at',
            computedValue: formatDate(activeRecord?.start_at),
          },
          {
            label: 'Дата окончание',
            key: 'end_at',
            computedValue: formatDate(activeRecord?.end_at),
          },
          {
            label: 'Статус',
            key: 'is_active',
            computedValue: activeRecord?.is_active ? 'Активный' : 'Не активный',
          },
          {
            label: 'Дата создания',
            key: 'created_date',
            computedValue: formatDate(activeRecord?.created_date),
          },
        ]}
        isVisible={drawerVisible}
        onClose={closeDrawer}
        onEdit={openEditForm}
        onDelete={handleDelete}
        deleteConfirmText="Вы точно хотите удалить эту запись ?"
      />

      <DiscountForm
        isActive={formIsActive}
        initialValues={activeRecord}
        onCreate={handleCreate}
        onUpdate={handleUpdate}
        onClose={closeForm}
      />
    </>
  );
}

export { Discount };
